import { observer, useLocalObservable } from 'mobx-react';
import React, { ReactNode, useEffect, useState } from 'react';
import { ApiAgent, ApiBusinessPartnerContact, ApiGetPasswordComplexityResponse } from '@ibe/api';
import { UncontrolledAlert } from 'reactstrap';
import { Element as ScrollTo } from 'react-scroll';
import BusinessPartnerInfo from '../../Components/BusinessPartner/BusinessPartnerInfo';
import useApi from '../../Hooks/useApi';
import Overlay from '../../Components/UI/LoadingOverlay/Overlay';
import OverlayContainer from '../../Components/UI/LoadingOverlay/OverlayContainer';
import useTranslation from '../../Hooks/useTranslation';
import fallback from '../../../_Translations/generated/agency-info-module.en.t.json';
import Keys from '../../../_Translations/generated/agency-info-module.en.t.json.keys';
import AgencyInfoStore from './AgencyInfoStore';
import PasswordChangeFormType from '../../Components/PasswordChange/PasswordChangeFormType';
import useAuth from '../../Hooks/useAuth';
import ScrollerService from '../../Services/ScrollerService';
import { useMount } from 'react-use';
import { LoggerFactory } from '@ibe/services';
import { TFunction } from 'i18next';

export interface Props {
  onPasswordChangeDone?: (isSuccessful: boolean) => void;
}

const logger = LoggerFactory.get('AgencyInfoModule');

const isNumber = (value: number | boolean): value is number => {
  return typeof value === 'number';
};

const buildContentEntry = (
  t: TFunction,
  key: string,
  value: number | boolean,
  optValue?: number
): string => {
  if (isNumber(value)) {
    return `<li>${t(key, optValue ? { count: value, sum: optValue } : { count: value })}<li/>`;
  }
  if (value) {
    return `<li>${t(key)}</li>`;
  }
  return '';
};

export const buildContent = (
  apiResponse: ApiGetPasswordComplexityResponse,
  t: TFunction
): string => {
  let result = `<ul class="list-unstyled"><li class="font-weight-bold">${t(
    Keys.passwordPolicyInformation
  )}</li>`;

  const entries = `${Object.entries(apiResponse)
    .filter(([key]) => key !== 'validationEnabled' && key !== 'amountCharRules')
    .map(([key, value]: [string, number | boolean]) => {
      return key === 'minMatchingCharRules'
        ? buildContentEntry(t, key, value, apiResponse.amountCharRules)
        : buildContentEntry(t, key, value);
    })
    .join(' ')}</ul>`;
  result += entries;

  return result;
};

const AgencyInfoModule = observer((props: Props) => {
  const api = useApi();
  const store = useLocalObservable(() => new AgencyInfoStore(api));
  const [notification, setNotification] = useState<ReactNode>(<></>);
  const [passwordComplexityInfo, setPasswordComplexityInfo] = useState<string | undefined>(
    undefined
  );
  const { t } = useTranslation('agency-info-module', fallback, Keys);
  const { authState } = useAuth();
  const { onPasswordChangeDone } = props;

  useEffect(() => {
    store.loadBusinessPartner();
    store.loadCountries();
    store.loadLanguages();
    store.loadCurrencies();
  }, [store]);

  useMount(async () => {
    store.setIsLoading(true);
    try {
      const apiResponse = await api.getPasswordComplexity();
      if (apiResponse.validationEnabled) {
        setPasswordComplexityInfo(buildContent(apiResponse, t));
      }
    } catch (e) {
      logger.error(e);
    } finally {
      store.setIsLoading(false);
    }
  });

  const handleSuccess = async (): Promise<void> => {
    await store.loadBusinessPartner();
    setNotification(<UncontrolledAlert color="success">{t(Keys.success)}</UncontrolledAlert>);
    store.setIsLoading(false);
    ScrollerService.scrollTo(scrollName);
  };

  const handleError = async (message: string | undefined): Promise<void> => {
    await store.loadBusinessPartner();
    setNotification(
      <UncontrolledAlert color="danger">{message || t(Keys.error)}</UncontrolledAlert>
    );
    store.setIsLoading(false);
    ScrollerService.scrollTo(scrollName);
  };

  const handleUserUpdate = async (agent: ApiAgent): Promise<void> => {
    store.setIsLoading(true);
    try {
      await api.editAgencyUser({ agent });
      await handleSuccess();
    } catch (e) {
      await handleError(undefined);
    }
  };

  const handleCompanyUpdate = async (agency: ApiBusinessPartnerContact): Promise<void> => {
    store.setIsLoading(true);
    try {
      await api.editBusinessPartner({ agency });
      await handleSuccess();
    } catch (e) {
      await handleError(undefined);
    }
  };

  const scrollName = 'agencyModTop';

  const handlePasswordChange = async (values: PasswordChangeFormType): Promise<void> => {
    store.setIsLoading(true);
    let isSuccessful = false;
    try {
      const apiResponse = await api.changePassword({
        newPassword: values.password,
        login: authState.user?.username || '',
        agencyNumber: authState.agency?.number || '',
        oldPassword: values.oldPassword
      });
      if (apiResponse.message) {
        await handleError(apiResponse.message);
      } else {
        await handleSuccess();
        isSuccessful = true;
      }
    } catch (e) {
      await handleError(undefined);
    } finally {
      if (onPasswordChangeDone) {
        onPasswordChangeDone(isSuccessful);
      }
    }
  };

  return (
    <>
      {store.businessPartner && (
        <>
          <OverlayContainer>
            <ScrollTo name={scrollName}>
              <BusinessPartnerInfo
                businessPartner={store.businessPartner}
                onUserUpdate={handleUserUpdate}
                onCompanyUpdate={handleCompanyUpdate}
                onPasswordChange={handlePasswordChange}
                notification={notification}
                countries={store.countries}
                languages={store.languages}
                currencies={store.currencies}
                passwordComplexityInfo={passwordComplexityInfo}
              />
            </ScrollTo>
            {store.isLoading && <Overlay />}
          </OverlayContainer>
        </>
      )}
    </>
  );
});

export default AgencyInfoModule;
